import { useState, useEffect, useCallback } from 'react';
import { databases } from '../../../services/appwriteService';
import { auditorService } from '../../../services/appwriteService';
import { Query } from 'appwrite';
import { 
  GlobalStats, 
  DetailedAuditorStats, 
  SessionProgress, 
  TaskDetail, 
  AdvancedFilters 
} from '../types/AdminTypes';

const appwriteDatabaseId = process.env.REACT_APP_APPWRITE_DATABASE_ID || 'focal-db';

interface LoadingStages {
  users: boolean;
  globalStats: boolean;
  auditorStats: boolean;
  sessions: boolean;
  tasks: boolean;
  isLoadingUsers: boolean;
  isLoadingGlobalStats: boolean;
  isLoadingAuditorStats: boolean;
  isLoadingSessions: boolean;
  isLoadingTasks: boolean;
  // Nouveaux états de progression
  progress: number;
  currentStep: string;
  totalSteps: number;
}

interface DashboardData {
  globalStats: GlobalStats;
  auditorStats: DetailedAuditorStats[];
  sessions: SessionProgress[];
  taskDetails: TaskDetail[];
  users: any[];
  loading: {
    initial: boolean;
    refetch: boolean;
  };
  error: string | null;
  loadingStages: LoadingStages;
  refetchData: () => Promise<void>;
  refetchUsers: () => Promise<void>;
}

export const useOptimizedDashboardData = (filters: AdvancedFilters): DashboardData => {
  // États des données
  const [globalStats, setGlobalStats] = useState<GlobalStats>({
    totalUsers: 0,
    totalAuditors: 0,
    activeAuditors: 0,
    todayAudits: 0,
    weekAudits: 0,
    monthAudits: 0,
    totalTasks: 0,
    validatedTasks: 0,
    commentedTasks: 0,
    rejectedTasks: 0,
    pendingTasks: 0,
    inProgressSessions: 0,
    globalValidationRate: 0,
    averageTasksPerAuditor: 0,
    averageProcessingTime: 0,
    topPerformer: '',
    productivityTrend: 0
  });

  const [auditorStats, setAuditorStats] = useState<DetailedAuditorStats[]>([]);
  const [sessions, setSessions] = useState<SessionProgress[]>([]);
  const [taskDetails, setTaskDetails] = useState<TaskDetail[]>([]);
  const [users, setUsers] = useState<any[]>([]);

  // États de chargement avec progression
  const [loading, setLoading] = useState({
    initial: true,
    refetch: false
  });

  const [error, setError] = useState<string | null>(null);
  const [loadingStages, setLoadingStages] = useState<LoadingStages>({
    users: false,
    globalStats: false,
    auditorStats: false,
    sessions: false,
    tasks: false,
    isLoadingUsers: false,
    isLoadingGlobalStats: false,
    isLoadingAuditorStats: false,
    isLoadingSessions: false,
    isLoadingTasks: false,
    progress: 0,
    currentStep: '',
    totalSteps: 4
  });

  // Cache simple
  const [cache, setCache] = useState<{
    users?: any[];
    usersTimestamp?: number;
    lastFilters?: string;
  }>({});

  const CACHE_DURATION = 60 * 1000;

  // Fonction pour mettre à jour la progression
  const updateProgress = useCallback((step: string, progress: number) => {
    setLoadingStages(prev => ({
      ...prev,
      currentStep: step,
      progress
    }));
  }, []);

  // ✅ FONCTION DE CHARGEMENT DES UTILISATEURS (avec cache et progression)
  const loadUsers = useCallback(async (forceRefresh = false) => {
    const now = Date.now();
    
    if (!forceRefresh && 
        cache.users && 
        cache.usersTimestamp && 
        (now - cache.usersTimestamp < CACHE_DURATION)) {
      console.log('📦 Cache utilisateurs utilisé');
      setUsers(cache.users);
      setLoadingStages(prev => ({ ...prev, users: true, isLoadingUsers: false }));
      return cache.users;
    }

    try {
      setLoadingStages(prev => ({ ...prev, isLoadingUsers: true }));
      updateProgress('Chargement des utilisateurs...', 10);
      
      const usersData = await auditorService.getAllAuditors();
      
      setUsers(usersData);
      setCache(prev => ({
        ...prev,
        users: usersData,
        usersTimestamp: now
      }));
      
      setLoadingStages(prev => ({ 
        ...prev, 
        users: true, 
        isLoadingUsers: false 
      }));
      
      updateProgress('Utilisateurs chargés', 25);
      console.log('✅ Utilisateurs chargés:', usersData.length);
      return usersData;
      
    } catch (error) {
      console.error('❌ Erreur chargement utilisateurs:', error);
      setLoadingStages(prev => ({ ...prev, isLoadingUsers: false }));
      throw error;
    }
  }, [cache.users, cache.usersTimestamp, updateProgress]);

  // ✅ FONCTION DE CHARGEMENT DES STATS GLOBALES (ultra-optimisée avec progression)
  const loadGlobalStats = useCallback(async () => {
    try {
      setLoadingStages(prev => ({ ...prev, isLoadingGlobalStats: true }));
      updateProgress('Calcul des statistiques globales RÉELLES...', 30);
  
      const today = new Date().toISOString().split('T')[0];
      const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      const monthAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  
      updateProgress('Récupération des comptes utilisateurs...', 35);
  
      // ✅ CORRECTION MAJEURE: Fonction de comptage par batch pour dépasser les limites Appwrite
      const countByBatches = async (collection: string, queries: any[] = []) => {
        let totalCount = 0;
        let offset = 0;
        const batchSize = 5000;
        let hasMore = true;
  
        while (hasMore) {
          try {
            const batchQuery = await databases.listDocuments(
              appwriteDatabaseId,
              collection,
              [
                ...queries,
                Query.select(['$id']),
                Query.limit(batchSize),
                Query.offset(offset)
              ]
            );
  
            const batchCount = batchQuery.documents.length;
            totalCount += batchCount;
            
            console.log(`📊 [GLOBAL-STATS] Batch ${Math.floor(offset/batchSize) + 1}: ${batchCount} documents (total: ${totalCount})`);
            
            if (batchCount < batchSize) {
              hasMore = false;
            } else {
              offset += batchSize;
            }
            
            // Sécurité
            if (offset > 100000) {
              console.warn('⚠️ Limite de sécurité atteinte à 100k documents');
              hasMore = false;
            }
            
          } catch (error) {
            console.error(`❌ Erreur batch ${Math.floor(offset/batchSize) + 1}:`, error);
            hasMore = false;
          }
        }
  
        return totalCount;
      };
  
      // ✅ Comptage des utilisateurs (simple car peu nombreux)
      const usersResponse = await databases.listDocuments(appwriteDatabaseId, 'users', [
        Query.select(['role', 'is_active']),
        Query.limit(200)
      ]);
  
      updateProgress('Comptage TOTAL des tâches auditées...', 40);
  
      // ✅ COMPTAGE RÉEL PAR BATCH des tâches
      const [
        totalTasks,
        validatedTasks,
        commentedTasks,
        pendingTasks,
        todayTasks,
        weekTasks,
        monthTasks
      ] = await Promise.all([
        // Total de toutes les tâches auditées
        countByBatches('audit_tasks', [
          Query.equal('audit_status', ['approved', 'rejected', 'pending'])
        ]),
        
        // Tâches approuvées
        countByBatches('audit_tasks', [
          Query.equal('audit_status', 'approved')
        ]),
        
        // Tâches rejetées/commentées  
        countByBatches('audit_tasks', [
          Query.equal('audit_status', 'rejected')
        ]),
        
        // Tâches en attente
        countByBatches('audit_tasks', [
          Query.equal('audit_status', 'pending')
        ]),
        
        // Tâches d'aujourd'hui
        countByBatches('audit_tasks', [
          Query.greaterThanEqual('audited_at', today + 'T00:00:00.000Z'),
          Query.equal('audit_status', ['approved', 'rejected'])
        ]),
        
        // Tâches de la semaine
        countByBatches('audit_tasks', [
          Query.greaterThanEqual('audited_at', weekAgo + 'T00:00:00.000Z'),
          Query.equal('audit_status', ['approved', 'rejected'])
        ]),
        
        // Tâches du mois
        countByBatches('audit_tasks', [
          Query.greaterThanEqual('audited_at', monthAgo + 'T00:00:00.000Z'),
          Query.equal('audit_status', ['approved', 'rejected'])
        ])
      ]);
  
      updateProgress('Comptage des sessions en cours...', 70);
  
      // ✅ Sessions en cours (simple car peu nombreuses)
      const inProgressSessionsResponse = await databases.listDocuments(appwriteDatabaseId, 'audit_sessions', [
        Query.equal('session_status', 'in_progress'),
        Query.select(['$id']),
        Query.limit(1)
      ]);
  
      updateProgress('Calcul des métriques finales...', 80);
  
      const usersData = usersResponse.documents;
      const totalUsers = usersData?.length || 0;
      const totalAuditors = usersData?.filter(u => u.role === 'auditor').length || 0;
      const activeAuditors = usersData?.filter(u => u.role === 'auditor' && u.is_active).length || 0;
      
      const inProgressSessions = inProgressSessionsResponse.total || 0;
      
      const processedTasks = validatedTasks + commentedTasks;
      const globalValidationRate = processedTasks > 0 ? Math.round((validatedTasks / processedTasks) * 100) : 0;
  
      const newGlobalStats = {
        totalUsers,
        totalAuditors,
        activeAuditors,
        todayAudits: todayTasks,
        weekAudits: weekTasks,
        monthAudits: monthTasks,
        totalTasks,          // ✅ VRAI TOTAL maintenant
        validatedTasks,      // ✅ VRAI TOTAL maintenant
        commentedTasks,      // ✅ VRAI TOTAL maintenant
        rejectedTasks: commentedTasks, // Alias pour rejected
        pendingTasks,        // ✅ VRAI TOTAL maintenant
        inProgressSessions,
        globalValidationRate,
        averageTasksPerAuditor: activeAuditors > 0 ? Math.round(totalTasks / activeAuditors) : 0,
        averageProcessingTime: 0,
        topPerformer: 'N/A',
        productivityTrend: globalValidationRate >= 80 ? 1 : globalValidationRate >= 60 ? 0 : -1
      };
  
      console.log('📊 [GLOBAL-STATS] Statistiques RÉELLES calculées:', {
        totalTasks,
        validatedTasks,
        commentedTasks,
        pendingTasks,
        globalValidationRate: `${globalValidationRate}%`
      });
  
      setGlobalStats(newGlobalStats);
      setLoadingStages(prev => ({ 
        ...prev, 
        globalStats: true, 
        isLoadingGlobalStats: false 
      }));
      
      updateProgress('Statistiques globales RÉELLES calculées', 90);
      console.log('✅ [GLOBAL-STATS] Stats globales RÉELLES terminées');
      
    } catch (error) {
      console.error('❌ Erreur stats globales RÉELLES:', error);
      setLoadingStages(prev => ({ ...prev, isLoadingGlobalStats: false }));
      
      // ✅ FALLBACK en cas d'erreur
      try {
        console.log('🔄 Fallback vers méthode simple...');
        
        const fallbackTotal = await databases.listDocuments(appwriteDatabaseId, 'audit_tasks', [
          Query.select(['$id']),
          Query.limit(1)
        ]);
        
        const estimatedTotal = fallbackTotal.total || 0;
        
        setGlobalStats(prev => ({
          ...prev,
          totalTasks: estimatedTotal,
          validatedTasks: Math.round(estimatedTotal * 0.7),
          commentedTasks: Math.round(estimatedTotal * 0.2),
          pendingTasks: Math.round(estimatedTotal * 0.1)
        }));
        
      } catch (fallbackError) {
        console.error('❌ Erreur fallback:', fallbackError);
      }
      
      throw error;
    }
  }, [updateProgress]);

  // ✅ FONCTION DE CHARGEMENT DES STATS AUDITEURS (avec progression détaillée)
  const loadAuditorStats = useCallback(async (usersData: any[]) => {
    try {
      setLoadingStages(prev => ({ ...prev, isLoadingAuditorStats: true }));
      updateProgress('Analyse RÉELLE des auditeurs...', 55);
  
      if (!usersData || usersData.length === 0) {
        setAuditorStats([]);
        setLoadingStages(prev => ({ 
          ...prev, 
          auditorStats: true, 
          isLoadingAuditorStats: false 
        }));
        return;
      }
  
      // ✅ FONCTION DE COMPTAGE RÉEL pour les auditeurs
      const countAuditorTasks = async (auditorId: string, status?: string) => {
        let totalCount = 0;
        let offset = 0;
        const batchSize = 2000;
        let hasMore = true;
      
        while (hasMore) {
          try {
            const queries = [
              Query.equal('auditor_id', auditorId), // ✅ CORRIGER audited_by → auditor_id
              Query.select(['$id']),
              Query.limit(batchSize),
              Query.offset(offset)
            ];
            
            if (status) {
              queries.push(Query.equal('audit_status', status));
            }
      
            const batchQuery = await databases.listDocuments(
              appwriteDatabaseId,
              'audit_tasks',
              queries
            );
      
            const batchCount = batchQuery.documents.length;
            totalCount += batchCount;
            
            if (batchCount < batchSize) {
              hasMore = false;
            } else {
              offset += batchSize;
            }
            
            if (offset > 20000) {
              hasMore = false;
            }
            
          } catch (error) {
            console.error(`❌ Erreur comptage auditeur ${auditorId}:`, error);
            hasMore = false;
          }
        }
      
        return totalCount;
      };
  
      const auditorsData = usersData.filter(u => u.role === 'auditor');
      const statsResults: DetailedAuditorStats[] = [];
      
      for (let i = 0; i < auditorsData.length; i++) {
        const auditor = auditorsData[i];
        const progressPercent = 55 + ((i / auditorsData.length) * 20);
        
        updateProgress(`Analyse RÉELLE: ${auditor.full_name} (${i + 1}/${auditorsData.length})`, progressPercent);
        
        try {
          if (i > 0) {
            await new Promise(resolve => setTimeout(resolve, 100));
          }
  
          // ✅ COMPTAGE RÉEL des tâches par auditeur
          const [totalTasks, validatedTasks, commentedTasks, sessionsCount] = await Promise.all([
            countAuditorTasks(auditor.$id),
            countAuditorTasks(auditor.$id, 'approved'),
            countAuditorTasks(auditor.$id, 'rejected'),
            
            databases.listDocuments(appwriteDatabaseId, 'audit_sessions', [
              Query.equal('auditor_id', auditor.$id),
              Query.select(['$id']),
              Query.limit(1)
            ]).then(res => res.total || 0).catch(() => 0)
          ]);
  
          const pendingTasks = totalTasks - validatedTasks - commentedTasks;
          const completedTasks = validatedTasks + commentedTasks;
          
          const validationRate = completedTasks > 0 ? Math.round((validatedTasks / completedTasks) * 100) : 0;
          const qualityScore = validationRate >= 90 ? 5 : validationRate >= 80 ? 4 : validationRate >= 70 ? 3 : validationRate >= 60 ? 2 : 1;
  
          const auditorStat: DetailedAuditorStats = {
            auditor_id: auditor.$id,
            auditor: {
              id: auditor.$id,
              full_name: auditor.full_name,
              email: auditor.email,
              is_active: auditor.is_active
            },
            totalTasks,            // ✅ VRAI TOTAL
            validatedTasks,        // ✅ VRAI TOTAL
            commentedTasks,        // ✅ VRAI TOTAL
            rejectedTasks: commentedTasks,
            pendingTasks,          // ✅ VRAI TOTAL
            completedTasks,
            totalSessions: sessionsCount,
            inProgressSessions: 0,
            validationRate,
            qualityScore,
            averageTasksPerDay: Math.round(totalTasks / 30),
            avgProcessingTime: 0
          };
  
          statsResults.push(auditorStat);
          
          console.log(`✅ [AUDITOR-STATS] ${auditor.full_name}: ${totalTasks} tâches (${validatedTasks} validées, ${commentedTasks} commentées)`);
  
        } catch (auditorError) {
          console.warn(`⚠️ Erreur ${auditor.full_name}:`, auditorError);
          // Fallback avec des zéros
          statsResults.push({
            auditor_id: auditor.$id,
            auditor: {
              id: auditor.$id,
              full_name: auditor.full_name,
              email: auditor.email,
              is_active: auditor.is_active
            },
            totalTasks: 0,
            validatedTasks: 0,
            commentedTasks: 0,
            rejectedTasks: 0,
            pendingTasks: 0,
            completedTasks: 0,
            totalSessions: 0,
            inProgressSessions: 0,
            validationRate: 0,
            qualityScore: 1,
            averageTasksPerDay: 0,
            avgProcessingTime: 0
          });
        }
      }
  
      const sortedStats = statsResults.sort((a, b) => b.totalTasks - a.totalTasks);
      setAuditorStats(sortedStats);
      
      setLoadingStages(prev => ({ 
        ...prev, 
        auditorStats: true, 
        isLoadingAuditorStats: false 
      }));
      
      updateProgress('Analyse RÉELLE des auditeurs terminée', 75);
      console.log('✅ [AUDITOR-STATS] Stats auditeurs RÉELLES chargées:', sortedStats.length);
      
    } catch (error) {
      console.error('❌ Erreur stats auditeurs RÉELLES:', error);
      setLoadingStages(prev => ({ ...prev, isLoadingAuditorStats: false }));
      throw error;
    }
  }, [updateProgress]);

  // ✅ FONCTION DE CHARGEMENT DES SESSIONS (avec progression)
  const loadSessions = useCallback(async () => {
    try {
      setLoadingStages(prev => ({ ...prev, isLoadingSessions: true }));
      updateProgress('Chargement des sessions...', 80);

      const queries = [
        Query.orderDesc('$createdAt'),
        Query.limit(50)
      ];

      if (filters.dateFrom) {
        const dateFromStart = filters.dateFrom + 'T00:00:00.000Z';
        queries.push(Query.greaterThanEqual('$createdAt', dateFromStart));
      }
      
      if (filters.dateTo) {
        const dateToEnd = filters.dateTo + 'T23:59:59.999Z';
        queries.push(Query.lessThanEqual('$createdAt', dateToEnd));
      }

      if (filters.auditorId) {
        queries.push(Query.equal('auditor_id', filters.auditorId));
      }
      
      if (filters.sessionStatus) {
        queries.push(Query.equal('session_status', filters.sessionStatus));
      }

      const sessionsResponse = await databases.listDocuments(
        appwriteDatabaseId,
        'audit_sessions',
        queries
      );

      const sessionsData = sessionsResponse.documents || [];
      updateProgress('Traitement des sessions...', 85);

      const sessionsWithStats: SessionProgress[] = [];
      
      for (let i = 0; i < Math.min(sessionsData.length, 20); i++) {
        const session = sessionsData[i];
        
        if (i > 0) {
          await new Promise(resolve => setTimeout(resolve, 50));
        }

        try {
          const sessionTasksResponse = await databases.listDocuments(
            appwriteDatabaseId,
            'audit_tasks',
            [
              Query.equal('session_id', session.$id),
              Query.limit(200),
              Query.select(['$id', 'audit_status', 'processing_time_seconds'])
            ]
          ).catch(() => ({ documents: [] }));

          const sessionTasks = sessionTasksResponse.documents || [];

          let auditorInfo = { full_name: 'Inconnu', email: '' };
          try {
            const auditorResponse = await databases.getDocument(
              appwriteDatabaseId,
              'users',
              session.auditor_id
            );
            auditorInfo = {
              full_name: auditorResponse.full_name || 'Inconnu',
              email: auditorResponse.email || ''
            };
          } catch (auditorError) {
            console.warn(`⚠️ Auditeur ${session.auditor_id} non trouvé`);
          }

          const totalTasks = sessionTasks.length;
          const completedTasks = sessionTasks.filter(t => t.audit_status !== 'pending').length;
          const validatedTasks = sessionTasks.filter(t => t.audit_status === 'approved').length;
          const rejectedTasks = sessionTasks.filter(t => t.audit_status === 'rejected').length;
          const progressPercentage = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

          const tasksWithTime = sessionTasks.filter(t => t.processing_time_seconds && t.processing_time_seconds > 0);
          const avgTimePerTask = tasksWithTime.length > 0
            ? Math.round(tasksWithTime.reduce((sum, t) => sum + (t.processing_time_seconds || 0), 0) / tasksWithTime.length)
            : 0;

          const currentTask = sessionTasks.find(t => t.audit_status === 'pending');

          const sessionProgress: SessionProgress = {
            session_id: session.$id,
            auditor: {
              id: session.auditor_id,
              full_name: auditorInfo.full_name,
              email: auditorInfo.email
            },
            session_status: session.session_status,
            created_at: session.$createdAt,
            total_tasks: totalTasks,
            completed_tasks: completedTasks,
            validated_tasks: validatedTasks,
            rejected_tasks: rejectedTasks,
            progress_percentage: progressPercentage,
            avg_time_per_task: avgTimePerTask,
            current_task_id: currentTask?.$id || undefined
          };

          sessionsWithStats.push(sessionProgress);

        } catch (sessionError) {
          console.warn(`⚠️ Erreur session ${session.$id}:`, sessionError);
        }
      }

      setSessions(sessionsWithStats);
      setLoadingStages(prev => ({ 
        ...prev, 
        sessions: true, 
        isLoadingSessions: false 
      }));
      
      updateProgress('Sessions chargées', 90);
      console.log('✅ Sessions chargées:', sessionsWithStats.length);
      
    } catch (error) {
      console.error('❌ Erreur sessions:', error);
      setLoadingStages(prev => ({ ...prev, isLoadingSessions: false }));
      throw error;
    }
  }, [filters, updateProgress]);

  // ✅ FONCTION DE CHARGEMENT DES TÂCHES (avec progression)
  const loadTasks = useCallback(async () => {
    try {
      setLoadingStages(prev => ({ ...prev, isLoadingTasks: true }));
      updateProgress('Chargement des tâches...', 95);

      const tasksQueries = [
        Query.orderDesc('$createdAt'),
        Query.limit(100),
        Query.select([
          '$id', 
          'agent_email', 
          'task_type', 
          'audit_status', 
          'audit_comment', 
          'processing_time_seconds', 
          'audited_at', 
          'audited_by',
          'session_id',
          '$createdAt'
        ])
      ];

      if (filters.dateFrom) {
        tasksQueries.push(Query.greaterThanEqual('audited_at', filters.dateFrom));
      }
      
      if (filters.dateTo) {
        tasksQueries.push(Query.lessThanEqual('audited_at', filters.dateTo));
      }

      if (filters.auditorId) {
        tasksQueries.push(Query.equal('audited_by', filters.auditorId));
      }

      const tasksResponse = await databases.listDocuments(
        appwriteDatabaseId,
        'audit_tasks',
        tasksQueries
      );

      const tasks = tasksResponse.documents || [];
      
      const mappedTasks: TaskDetail[] = tasks.map(task => ({
        task_id: task.$id,
        agent_email: task.agent_email || '',
        task_type: task.task_type || '',
        audit_status: task.audit_status || 'pending',
        audit_comment: task.audit_comment || '',
        processing_time_seconds: task.processing_time_seconds || 0,
        created_at: task.$createdAt,
        audited_at: task.audited_at || '',
        audited_by: task.auditor_id || '',
        session_id: task.session_id || ''
      }));
      
      setTaskDetails(mappedTasks);
      
      setLoadingStages(prev => ({ 
        ...prev, 
        tasks: true, 
        isLoadingTasks: false 
      }));
      
      updateProgress('Tâches chargées', 100);
      console.log('✅ Tâches chargées:', mappedTasks.length);
      
    } catch (error) {
      console.error('❌ Erreur tâches:', error);
      setLoadingStages(prev => ({ ...prev, isLoadingTasks: false }));
      throw error;
    }
  }, [filters, updateProgress]);

  // ✅ FONCTION PRINCIPALE DE CHARGEMENT (avec progression globale)
  const loadAllData = useCallback(async (forceRefreshUsers = false) => {
    try {
      setError(null);
      setLoading(prev => ({ ...prev, initial: true }));
      updateProgress('Initialisation...', 0);
      
      console.log('🚀 Démarrage chargement optimisé dashboard...');

      // 1. Charger les utilisateurs d'abord
      const usersData = await loadUsers(forceRefreshUsers);
      
      // 2. Charger les stats globales
      await loadGlobalStats();
      
      // 3. Charger les autres données en parallèle (non-bloquant)
      Promise.all([
        loadAuditorStats(usersData),
        loadSessions(),
        loadTasks()
      ]).catch(error => {
        console.warn('⚠️ Erreur chargement données secondaires:', error);
      });

      setLoading(prev => ({ ...prev, initial: false }));
      updateProgress('Chargement terminé', 100);
      console.log('✅ Chargement principal terminé');
      
    } catch (error) {
      console.error('❌ Erreur chargement dashboard:', error);
      setError(error instanceof Error ? error.message : 'Erreur de chargement');
      setLoading(prev => ({ ...prev, initial: false }));
    }
  }, [loadUsers, loadGlobalStats, loadAuditorStats, loadSessions, loadTasks, updateProgress]);

  // ✅ FONCTION DE RECHARGEMENT (avec progression)
  const refetchData = useCallback(async () => {
    try {
      setLoading(prev => ({ ...prev, refetch: true }));
      updateProgress('Actualisation des données...', 10);
      
      console.log('🔄 Rechargement données avec nouveaux filtres...');
      
      updateProgress('Rechargement des auditeurs...', 30);
      await loadAuditorStats(users);
      
      updateProgress('Rechargement des sessions...', 60);
      await loadSessions();
      
      updateProgress('Rechargement des tâches...', 90);
      await loadTasks();
      
      setLoading(prev => ({ ...prev, refetch: false }));
      updateProgress('Actualisation terminée', 100);
      console.log('✅ Rechargement terminé');
      
    } catch (error) {
      console.error('❌ Erreur rechargement:', error);
      setLoading(prev => ({ ...prev, refetch: false }));
    }
  }, [users, loadAuditorStats, loadSessions, loadTasks, updateProgress]);

  // ✅ FONCTION DE RECHARGEMENT DES UTILISATEURS
  const refetchUsers = useCallback(async () => {
    await loadUsers(true);
  }, [loadUsers]);

  // ✅ EFFET INITIAL
  useEffect(() => {
    loadAllData();
  }, []);

  // ✅ EFFET SUR CHANGEMENT DE FILTRES (debounced avec progression)
  useEffect(() => {
    const filtersString = JSON.stringify(filters);
    
    if (cache.lastFilters && cache.lastFilters === filtersString) {
      return;
    }

    const timer = setTimeout(() => {
      console.log('🔄 Filtres modifiés, rechargement...');
      setCache(prev => ({ ...prev, lastFilters: filtersString }));
      refetchData();
    }, 300);

    return () => clearTimeout(timer);
  }, [filters, refetchData, cache.lastFilters]);

  return {
    globalStats,
    auditorStats,
    sessions,
    taskDetails,
    users,
    loading,
    error,
    loadingStages,
    refetchData,
    refetchUsers
  };
};